const PGAdapter = require('./../../common/pg-adapter')
const {
  getPositionalArgs,
  matchAllValuesILikePreparer,
  matchAnyValueILikePreparer,
  arrayOrStringPreparer,
  qualifiedArrayOrStringPreparer
} = require('./../../tools/sql-tools')
const { getPendingDeliveriesQuery } = require('./queries')

const DATA_SHIPMENT_TABLE_NAME = 'avocado.data_shipment'
const DATA_SHIPMENT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
  'notes',
  'destination_id',
  'origin_id',
  'planning_type',
  'order_id',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'suborder_id'
]

const DATA_SHIPMENT_VIRTUAL_COLUMNS = [
  'substring(destination_id from \'state:([^:]+)\')',
  'updated_at_gte',
  'updated_at_lte',
  'created_at_gte',
  'created_at_lte',
  'carrier_id',
  'shipment_no_sort',
  'origin_name_sort',
  'destination_name_sort',
  'funder_name_sort',
  'program_name_sort',
  'status_sort'
]

const SORTABLE_LMD_COLUMNS = new Set([
  'carrier_id',
  'shipment_no_sort',
  'origin_name_sort',
  'destination_name_sort',
  'funder_name_sort',
  'program_name_sort',
  'status_sort'
])

const DATA_SHIPMENTLINE_TABLE_NAME = 'avocado.data_shipmentline'

class DataShipmentAdapter extends PGAdapter {
  static get SHIPMENT_TYPE_LAST_MILE_DELIVERY () {
    return 'last_mile_delivery'
  }

  static get SHIPMENT_TYPE_FROM_WAREHOUSE () {
    return 'from_warehouse'
  }

  static get SHIPMENT_TYPE_ALL_TYPES () {
    return 'all_types'
  }

  static get shipmentTypeFilters () {
    return {
      [this.SHIPMENT_TYPE_LAST_MILE_DELIVERY]: {
        origin_id: '%:name:%', // warehouse
        destination_id: '%:sdp:%'// Health facility
      },
      [this.SHIPMENT_TYPE_FROM_WAREHOUSE]: {
        origin_id: '%:name:%' // warehouse
      },
      [this.SHIPMENT_TYPE_ALL_TYPES]: { } // No restrictions
    }
  }

  static get allShipmentsFilter () {
    return { } // No restrictions
  }

  constructor (pgConnection, user, logger, options = {}) {
    const opts = {
      getRelated: {
        'lines': {
          relatedTable: DATA_SHIPMENTLINE_TABLE_NAME,
          fk: 'shipment_id'
        }
      },
      virtualColumns: DATA_SHIPMENT_VIRTUAL_COLUMNS,
      withOrgContext: true,
      withGlobalAccessUser: options.withGlobalAccessUser
    }

    const columnPreparers = {
      // origin_id uses OR semantics: the LMD Source filter can hold several
      // real warehouse ids, which must match as alternatives (issue #23868);
      // each substring match covers the warehouse and all of its funder-split
      // virtual copies. The warehouse-origin type restriction ('%:name:%') is
      // dropped before the merge when a user source is present (see
      // shipment/index.js listLastMileDeliveries), so ANY here never weakens
      // that guard.
      origin_id: matchAnyValueILikePreparer,
      destination_id: matchAllValuesILikePreparer,
      shipment_id: arrayOrStringPreparer,
      funder_id: arrayOrStringPreparer,
      program_id: arrayOrStringPreparer,
      status: qualifiedArrayOrStringPreparer(DATA_SHIPMENT_TABLE_NAME),
      route_id: arrayOrStringPreparer, // CRITICAL: Handle route_id filtering, including empty arrays for drivers with no routes
      created_at: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."${key}">=%INDEX%`, value}],
      updated_at_gte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."updated_at">=%INDEX%`, value: value}],
      updated_at_lte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."updated_at"<=%INDEX%`, value: value}],
      created_at_gte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."created_at">=%INDEX%`, value: value}],
      created_at_lte: (key, value) => [{statement: `${DATA_SHIPMENT_TABLE_NAME}."created_at"<=%INDEX%`, value: value}],
      // Calculated column: default preparer without adding double quotes to the key
      'substring(destination_id from \'state:([^:]+)\')': (key, value) => [{statement: `${key}=%INDEX%`, value}],
      carrier_id: (key, value) => [{statement: `avocado.data_route."carrier_id" = %INDEX%`, value}]
    }

    super(
      pgConnection,
      DATA_SHIPMENT_TABLE_NAME,
      user,
      DATA_SHIPMENT_COLUMNS,
      undefined,
      columnPreparers,
      logger,
      opts
    )
  }

  shouldUseSortableLmdTableExpression (ordering, filter = {}) {
    if (typeof filter.carrier_id !== 'undefined') {
      return true
    }

    const orderings = Array.isArray(ordering) ? ordering : [ordering]
    return orderings
      .filter(Boolean)
      .map(order => order.replace(/^-/, ''))
      .some(order => SORTABLE_LMD_COLUMNS.has(order))
  }

  getSortableLmdTableExpression () {
    return `
      (
        SELECT
          ${this.tableName}.*,
          avocado.data_route."carrier_id" AS carrier_id,
          COALESCE(
            NULLIF(${this.tableName}."shipment_number", ''),
            substring(${this.tableName}."shipment_id" from 'shipmentNo:([^:]+)$')
          ) AS shipment_no_sort,
          LOWER(
            COALESCE(
              origin_location."name_of_pharmacy",
              REPLACE(substring(${this.tableName}."origin_id" from 'name:([^:]+)$'), '-', ' '),
              ${this.tableName}."origin_id"
            )
          ) AS origin_name_sort,
          LOWER(
            COALESCE(
              destination_location."name_of_pharmacy",
              REPLACE(substring(${this.tableName}."destination_id" from 'sdp:([^:]+)$'), '-', ' '),
              ${this.tableName}."destination_id"
            )
          ) AS destination_name_sort,
          LOWER(COALESCE(REPLACE(${this.tableName}."funder_id", 'funder:', ''), '')) AS funder_name_sort,
          LOWER(
            COALESCE(
              shipment_program."name",
              REPLACE(${this.tableName}."program_id", 'program:', ''),
              ''
            )
          ) AS program_name_sort,
          LOWER(COALESCE(${this.tableName}."status", '')) AS status_sort
        FROM ${this.tableName}
        LEFT JOIN avocado.data_route
          ON avocado.data_route."couchdb_id" = ${this.tableName}."route_id"
        LEFT JOIN avocado.data_location origin_location
          ON origin_location."fsid" = ${this.tableName}."origin_id"
        LEFT JOIN avocado.data_location destination_location
          ON destination_location."fsid" = ${this.tableName}."destination_id"
        LEFT JOIN avocado.data_program shipment_program
          ON shipment_program."fsid" = ${this.tableName}."program_id"
      ) AS lmd_shipments
    `
  }

  getSortableLmdTablePreparers () {
    return {
      status: arrayOrStringPreparer,
      carrier_id: arrayOrStringPreparer,
      created_at: (key, value) => [{ statement: `"${key}">=%INDEX%`, value }],
      updated_at_gte: (key, value) => [{ statement: `"updated_at">=%INDEX%`, value }],
      updated_at_lte: (key, value) => [{ statement: `"updated_at"<=%INDEX%`, value }],
      created_at_gte: (key, value) => [{ statement: `"created_at">=%INDEX%`, value }],
      created_at_lte: (key, value) => [{ statement: `"created_at"<=%INDEX%`, value }]
    }
  }

  async getList (args = {}) {
    const {
      addGroupOrderId,
      ordering = this.idColumn,
      filter = {},
      limit = 50,
      offset = 0,
      search = null,
      skipCount = false,
      skipData = false,
      getRelated,
      client,
      customPreparers
    } = args

    const shouldUseSortableLmdTableExpression = this.shouldUseSortableLmdTableExpression(ordering, filter)
    const relatedEntitiesConfig = {
      ...(getRelated || this.getRelated)
    }

    if (addGroupOrderId) {
      relatedEntitiesConfig.order = {
        relatedTable: 'avocado.data_order',
        pk: 'order_id',
        fk: 'order_id',
        fields: ['group_id']
      }
    }

    const { carrier_id: carrierId } = filter
    if (typeof carrierId !== 'undefined') {
      relatedEntitiesConfig.route = {
        relatedTable: 'avocado.data_route',
        fk: 'couchdb_id',
        pk: 'route_id',
        fields: ['carrier_id']
      }
    }

    // The sortable table expression is a sub-select aliased `lmd_shipments`,
    // so its WHERE clause must be built from the matching unqualified
    // preparers. Thread them through getList (not via adapter state, which
    // cannot survive the batched path's awaits) and let caller-supplied
    // preparers override them, as the base getWhereCondition merge promises.
    const sortableCustomPreparers = shouldUseSortableLmdTableExpression
      ? { ...this.getSortableLmdTablePreparers(), ...(customPreparers || {}) }
      : customPreparers

    return super.getList({
      ordering,
      filter,
      limit,
      offset,
      search,
      skipCount,
      skipData,
      getRelated: relatedEntitiesConfig,
      client,
      customPreparers: sortableCustomPreparers,
      ...(shouldUseSortableLmdTableExpression
        ? { tableExpression: this.getSortableLmdTableExpression() }
        : {})
    })
  }

  /**
   * @param {Object} param
   * @param {string} param.locationId
   * @param {number} param.weeks
   * @param {any} param.pgClient
   * @returns {Promise<{locationId: string, shipmentId: string, deliveryAmount: number, deliveryInvoiceAmount: number}[]>}
   */
  async getPendingDeliveries ({locationId, weeks, pgClient}) {
    const query = getPendingDeliveriesQuery(weeks)
    const client = pgClient || this.pgConnection
    const { rows } = await client.query(query, [locationId])
    return rows
  }

  async getShipments ({
    originId,
    destinationId,
    status = [],
    weeks = 0 // get shipments in the past n weeks
  }) {
    if (!originId && !destinationId) {
      throw new Error(`at least one of 'originId' or 'destinationId' is required`)
    }
    const params = []
    let query = `
      SELECT
        s.id,
        s.shipment_id,
        s.status,
        s.origin_id,
        s.destination_id,
        s.planning_type,
        s.order_id,
        concat('product:', sl.sku) as sku,
        sl.amount as quantity,
        s.created_at,
        s.updated_at
      FROM ${DATA_SHIPMENT_TABLE_NAME} s
      LEFT JOIN ${DATA_SHIPMENTLINE_TABLE_NAME} sl on s.id = sl.shipment_id
      WHERE
    `
    if (originId) {
      params.push(originId)
      query = query.concat(` origin_id = $${params.length}`)
    }
    if (destinationId && params.length >= 1) {
      params.push(destinationId)
      query = query.concat(` AND destination_id = $${params.length}`)
    } else if (destinationId && params.length === 0) {
      params.push(destinationId)
      query = query.concat(` destination_id = $${params.length}`)
    }
    if (status.length > 0) {
      const start = params.length + 1
      const args = getPositionalArgs(status, start)
      params.push(...status)
      query = query.concat(` AND status IN (${args})`)
    }
    if (typeof (weeks) === 'number') {
      query = query.concat(` AND s.created_at + '${weeks} week'::INTERVAL >= current_date order by s.created_at`)
    }
    const { rows } = await this.pgConnection.query(query, params)
    return rows
  }

  async getLastMileDeliveriesFilterValues (selectedFilter) {
    const tableName = 'data_shipment'
    const filteringTableColumns = [
      'program_id',
      'funder_id',
      // TODO, we don't sync this prop yet to RDS
      // 'delivery_date',
      'origin_id',
      'destination_id',
      'carrier_id'
    ]

    // route_id is projected as { id, name } so the LMD page can render route
    // filter labels directly from this response. Resolving names client-side
    // via /agave/v1/route/?couchdb_id=... fails on months with many distinct
    // routes because the resulting URL is too long (issue #23931). The
    // LEFT JOIN to data_route is already in the table expression below, so
    // adding the name here is free.
    const filteringCalculatedColumns = [
      { name: 'state', expression: 'substring(destination_id from \'state:([^:]+)\')' },
      { name: 'status', expression: `${this.tableName}.status` },
      { name: 'created_at', expression: `${this.tableName}.created_at` },
      {
        name: 'route_id',
        expression: `CASE WHEN avocado.${tableName}.route_id IS NULL THEN NULL ELSE jsonb_build_object('id', avocado.${tableName}.route_id, 'name', avocado.data_route.name) END`
      }
    ]

    const stateFilter = {}
    let removeDestinationIdFilter = false
    if (selectedFilter.state) {
      stateFilter['substring(destination_id from \'state:([^:]+)\')'] = selectedFilter.state
      delete selectedFilter.state
      removeDestinationIdFilter = true
    }

    // Use shipmentTypeFilters for last mile delivery filtering
    const lmdFilter = DataShipmentAdapter.shipmentTypeFilters[DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY]
    const preFilter = {...lmdFilter, ...selectedFilter, ...stateFilter}
    if (removeDestinationIdFilter) {
      delete preFilter.destination_id
    }

    return this.getColumnsUniqueValues(
      filteringTableColumns,
      filteringCalculatedColumns,
      preFilter,
      `avocado.${tableName} left join avocado.data_route on avocado.data_route.couchdb_id = avocado.${tableName}.route_id`,
      this.columnPreparers
    )
  }

  async getShipmentProgramId (shipmentId) {
    if (!shipmentId) {
      throw new Error('shipmentId required to get program id')
    }

    const { rows } = await this.pgConnection.query(`
      select
        coalesce(o.program_id, s.program_id) as id,
        replace(coalesce(o.program_id, s.program_id), 'program:', '') as name
      from avocado.data_shipment s
      inner join avocado.data_order o on s.order_id = o.order_id and s.destination_id = o.destination_id
      where s.shipment_id = $1
    `, [shipmentId])
    return rows[0]
  }

  async getVolume (shipmentIds, { batchProductSkuById = {}, preservePrecision = false } = {}) {
    // `batchProductSkuById` maps a raw batch string to the manufacturer-specific
    // product SKU it resolves to via the batch doc's alias (the `:manufacturer:`
    // segment is often a name, not a code). Callers resolve it (issue #26027) -
    // `ShipmentApi.calculateVolume` resolves on the caller's behalf when the map
    // is omitted. Note this is NOT "same as before" for an empty map: with no
    // map every row resolves to the generic product, whereas the pre-#26027
    // query inferred the manufacturer product from the `:manufacturer:` segment.
    const resolvedRows = Object.entries(batchProductSkuById).map(([batch, sku]) => ({ batch, sku }))
    const { rows } = await this.pgConnection.query(
      `WITH resolved AS (
          SELECT * FROM jsonb_to_recordset($2::jsonb) AS r(batch text, sku text)
        )
        SELECT
          s.shipment_id AS shipment_id,
          -- The automatic planner needs the unrounded total: it distinguishes
          -- a valid tiny volume from missing volumetrics. Other callers retain
          -- the established six-decimal presentation precision.
          CASE
            WHEN $3::BOOLEAN THEN COALESCE(SUM(
              COALESCE(sld.amount, sl.amount, ssld.amount, ssl.amount, 0) * COALESCE(vol.unit_volume, 0)
            ), 0)::NUMERIC
            ELSE ROUND(COALESCE(SUM(
              COALESCE(sld.amount, sl.amount, ssld.amount, ssl.amount, 0) * COALESCE(vol.unit_volume, 0)
            ), 0)::NUMERIC, 6)
          END AS total_volume
        FROM avocado.data_shipment s
        LEFT JOIN avocado.data_shipmentline sl ON sl.shipment_id = s.id
        LEFT JOIN avocado.data_shipmentline_details sld ON sld.shipment_line = sl.id
        -- Fall back to initial 'new' snapshot lines when no regular shipment lines exist.
        -- This handles newly created shipments where data_shipmentline may not be populated yet.
        LEFT JOIN LATERAL (
          SELECT ss.id AS snapshot_id
          FROM avocado.data_shipmentsnapshot ss
          WHERE ss.shipment_id = s.shipment_id
            AND ss.status = 'new'
            AND sl.id IS NULL
          ORDER BY ss.created_at ASC
          LIMIT 1
        ) first_snapshot ON true
        LEFT JOIN avocado.data_shipmentsnapshotline ssl
          ON ssl.shipment_snapshot_id = first_snapshot.snapshot_id
        LEFT JOIN avocado.data_shipmentsnapshotline_details ssld
          ON ssld.shipmentsnapshot_line = ssl.id
        -- Batch string, used to join the caller-supplied alias-resolved SKUs.
        LEFT JOIN LATERAL (
          SELECT COALESCE(sld.batch, ssld.batch) AS batch_string
        ) batch_info ON true
        -- Alias-resolved manufacturer SKU for this batch (from the batch doc's
        -- alias). There is deliberately NO fallback to the raw :manufacturer:
        -- segment: that segment is a manufacturer name for many integrations
        -- and inferring a product from it is incorrect (issue #26027).
        LEFT JOIN resolved ON resolved.batch = batch_info.batch_string
        -- Resolve unitVolume with one priority lookup per row:
        --   1. manufacturer-specific product (alias-resolved SKU only)
        --   2. generic product (line sku, or snapshot-line sku as fallback)
        -- COALESCE runs the generic subquery only when the manufacturer arm is NULL.
        -- That covers both "no alias-resolved SKU" and "manufacturer SKU exists but
        -- its presentation volume is NULL". Each arm is a point lookup on the
        -- covering index (0480: LOWER(old_sku_id), id); ORDER BY ps.id ASC LIMIT 1
        -- keeps the lowest-id winner when the same product exists with different
        -- casing. LOWER on both sides handles any case mismatch between PG and CouchDB data.
        LEFT JOIN LATERAL (
          SELECT COALESCE(
            (SELECT pp.volume::DOUBLE PRECISION
             FROM avocado.data_productsku ps
             LEFT JOIN avocado.data_productpresentation pp ON pp.id = ps.presentation_id
             WHERE ps._deleted IS NOT TRUE
               AND LOWER(ps.old_sku_id) = LOWER(resolved.sku)
             ORDER BY ps.id ASC
             LIMIT 1),
            (SELECT pp.volume::DOUBLE PRECISION
             FROM avocado.data_productsku ps
             LEFT JOIN avocado.data_productpresentation pp ON pp.id = ps.presentation_id
             WHERE ps._deleted IS NOT TRUE
               AND LOWER(ps.old_sku_id) = LOWER(COALESCE(sl.sku, ssl.sku))
             ORDER BY ps.id ASC
             LIMIT 1)
          ) AS unit_volume
        ) vol ON true
        WHERE s.shipment_id = ANY($1)
        GROUP BY s.shipment_id`,
      [shipmentIds, JSON.stringify(resolvedRows), preservePrecision]
    )
    if (!rows) {
      return []
    }
    return rows
  }

  /**
   * Distinct batch strings referenced by a set of shipments (from the regular
   * shipment lines, or the initial 'new' snapshot lines as fallback). Used to
   * resolve manufacturer-specific products via the batch alias before the
   * volume query (issue #26027).
   * @param {string[]} shipmentIds
   * @returns {Promise<string[]>}
   */
  async getBatchStringsForShipments (shipmentIds) {
    if (!shipmentIds || !shipmentIds.length) return []
    const { rows } = await this.pgConnection.query(`
      SELECT DISTINCT COALESCE(sld.batch, ssld.batch) AS batch
      FROM avocado.data_shipment s
      LEFT JOIN avocado.data_shipmentline sl ON sl.shipment_id = s.id
      LEFT JOIN avocado.data_shipmentline_details sld ON sld.shipment_line = sl.id
      LEFT JOIN LATERAL (
        SELECT ss.id AS snapshot_id
        FROM avocado.data_shipmentsnapshot ss
        WHERE ss.shipment_id = s.shipment_id
          AND ss.status = 'new'
          AND sl.id IS NULL
        ORDER BY ss.created_at ASC
        LIMIT 1
      ) first_snapshot ON true
      LEFT JOIN avocado.data_shipmentsnapshotline ssl
        ON ssl.shipment_snapshot_id = first_snapshot.snapshot_id
      LEFT JOIN avocado.data_shipmentsnapshotline_details ssld
        ON ssld.shipmentsnapshot_line = ssl.id
      WHERE s.shipment_id = ANY($1)
        AND COALESCE(sld.batch, ssld.batch) IS NOT NULL`,
    [shipmentIds])
    return rows.map(row => row.batch)
  }

  /**
   * Retrieves last mile deliveries shipments for download based on provided filters.
   * Includes carrier name by joining with data_route and data_organisation.
     *
     * @param {Object} filters - The filter object.
     * @param {string} filters.created_at_gte - Start datetime for created_at (ISO string). Required.
     * @param {string} filters.created_at_lte - End datetime for created_at (ISO string). Required.
     * @param {string} [filters.origin_id] - Origin ID filter; values are substring-matched like the on-screen list (issue 25168).
     * @param {string} [filters.destination_id] - Destination ID filter; values are substring-matched like the on-screen list.
     * @returns {Promise<Array<Object>>} A promise that resolves to an array of shipment records.
     * @throws {Error} If created_at_gte or created_at_lte are missing.
     */
  async getLastMileDeliveriesShipmentsForDownload (filters) {
    const {
      created_at_gte: createdAtGte,
      created_at_lte: createdAtLte,
      created_at: createdAt,
      origin_id: originId,
      destination_id: destinationId,
      program_id: programId,
      carrier_id: carrierId,
      route_id: routeId,
      funder_id: funderId,
      status,
      state
    } = filters

    const hasTimeRange = createdAtGte && createdAtLte
    const hasCreatedAt = createdAt

    if (!hasTimeRange && !hasCreatedAt) {
      throw new Error('Either provide a time range (created_at_gte & created_at_lte) or a specific created_at date')
    }

    const isValidDate = (dateStr) => !isNaN(Date.parse(dateStr))

    if (hasTimeRange && (!isValidDate(createdAtGte) || !isValidDate(createdAtLte))) {
      throw new Error('Invalid date format for created_at_gte or created_at_lte')
    }

    if (hasCreatedAt && !isValidDate(createdAt)) {
      throw new Error('Invalid date format for created_at')
    }

    const mappings = {
      created_at_gte: ['s.created_at', '>='],
      created_at_lte: ['s.created_at', '<='],
      updated_at_gte: ['s.updated_at', '>='],
      updated_at_lte: ['s.updated_at', '<='],
      origin_id: ['s.origin_id', 'ILIKE'],
      destination_id: ['s.destination_id', 'ILIKE'],
      program_id: ['s.program_id', '='],
      carrier_id: ['r.carrier_id', '='],
      route_id: ['s.route_id', '='],
      funder_id: ['s.funder_id', '='],
      status: ['s.status', '='],
      state: ['substring(s.destination_id from \'state:([^:]+)\')', '=']
    }

    const buildCreatedAtCondition = (dateStr) => {
      const start = new Date(dateStr)
      start.setUTCHours(0, 0, 0, 0)

      const end = new Date(dateStr)
      end.setUTCHours(23, 59, 59, 999)

      return {
        condition: `s.created_at >= $1 AND s.created_at <= $2`,
        params: [start.toISOString(), end.toISOString()]
      }
    }

    // Apply LMD filter internally (original behavior - this method always filters for last mile deliveries)
    // The filters passed in are merged with the LMD filter, with passed filters taking precedence
    const lmdFilter = DataShipmentAdapter.shipmentTypeFilters[DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY]
    const effectiveFilters = {
      ...lmdFilter,
      ...(!hasCreatedAt && hasTimeRange && { created_at_gte: createdAtGte, created_at_lte: createdAtLte }),
      ...(originId && { origin_id: originId }),
      ...(destinationId && { destination_id: destinationId }),
      ...(programId && { program_id: programId }),
      ...(carrierId && { carrier_id: carrierId }),
      // CRITICAL: Include route_id even if it's an empty array (for drivers with no routes)
      // Empty array means "match no shipments" which is the correct behavior
      ...(routeId !== undefined && { route_id: routeId }),
      ...(funderId && { funder_id: funderId }),
      ...(status && { status }),
      ...(state && { state })
    }

    const buildQueryConditions = () => {
      const conditions = []
      const params = []

      if (hasCreatedAt) {
        const { condition, params: dateParams } = buildCreatedAtCondition(createdAt)
        conditions.push(condition)
        params.push(...dateParams)
      }

      Object.entries(effectiveFilters).forEach(([key, value]) => {
        if (!mappings[key]) return

        // CRITICAL: Allow empty arrays to pass through (they represent "match nothing" filters)
        // Empty arrays are falsy in JavaScript, but we need them for route_id filtering
        if (value === undefined || value === null) return

        const [column, operator] = mappings[key]

        if (operator === 'ILIKE') {
          // origin_id and destination_id go through the same preparers as the
          // on-screen list (matchAnyValueILikePreparer / matchAllValuesILikePreparer),
          // qualified to this query's `s` alias, so single and multi value filters
          // substring-match exactly as the list does (issue 25168). A single
          // origin_id resolved to its real warehouse id matches the funder-split
          // virtual copies stored on shipments (warehouse-id:funder:...); a
          // caller-value already carrying a wildcard (exportWareHouseFunderShipments
          // passes '<id>%') keeps working since the wrapping happens in SQL.
          const [{ statement, value: clauseValue }] = this.columnPreparers[key](`s.${key}`, value)
          conditions.push(statement.replace('%INDEX%', `$${params.length + 1}`))
          params.push(clauseValue)
          return
        }

        if (Array.isArray(value)) {
          // Empty arrays mean "match no rows" - valid for route_id filtering
          conditions.push(`${column} = ANY($${params.length + 1}::text[])`)
          params.push(value)
        } else {
          conditions.push(`${column} ${operator} $${params.length + 1}`)
          params.push(value)
        }
      })
      return { params, whereConditions: conditions }
    }

    const { params, whereConditions } = buildQueryConditions()

    const whereClause = whereConditions.join(' AND ')

    const query = `
    WITH filtered_shipments AS (
      SELECT s.id, s.shipment_id, s.order_id
      FROM avocado.data_shipment s
      LEFT JOIN avocado.data_route r ON s.route_id = r.couchdb_id
      WHERE ${whereClause}
    ),
    order_lookup AS (
      SELECT DISTINCT ON (o.order_id)
        o.order_id,
        o.group_id
      FROM avocado.data_order o
      WHERE o.group_id IS NOT NULL
        AND o.order_id IN (SELECT order_id FROM filtered_shipments WHERE order_id IS NOT NULL)
    ),
    snapshot_dates_agg AS (
      SELECT
        ss.shipment_id,
        jsonb_object_agg(ss.status, ss.earliest_date) as snapshot_dates
      FROM (
        SELECT shipment_id, status, MIN(created_at)::text as earliest_date
        FROM avocado.data_shipmentsnapshot
        WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
        GROUP BY shipment_id, status
      ) ss
      GROUP BY ss.shipment_id
    ),
    driver_lookup AS (
      SELECT DISTINCT ON (shipment_id)
        shipment_id,
        created_by as driver_name
      FROM avocado.data_shipmentsnapshot
      WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
        AND status = 'received'
      ORDER BY shipment_id, created_at ASC
    ),
    snapshot_lines AS (
      SELECT
        ssl.shipment_snapshot_id,
        jsonb_object_agg(
          COALESCE(ssld.batch, CONCAT('product:', ssl.sku, ':manufacturer:unknown:batchNo:UNKNOWN')),
          jsonb_build_object(
            'quantity', COALESCE(ssld.amount, ssl.amount),
            'checked', ssl.checked
          )
        ) as counts
      FROM avocado.data_shipmentsnapshotline ssl
      LEFT JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
      WHERE ssl.shipment_snapshot_id IN (
        SELECT ss.id
        FROM avocado.data_shipmentsnapshot ss
        WHERE ss.shipment_id IN (SELECT shipment_id FROM filtered_shipments)
      )
      GROUP BY ssl.shipment_snapshot_id
    ),
    history_agg AS (
      SELECT
        ss.shipment_id,
        jsonb_object_agg(
          CONCAT(ss.shipment_id, ':status:', ss.status, ':agent:'),
          jsonb_build_object(
            'createdAt', ss.created_at::text,
            'createdBy', jsonb_build_object('user', ss.created_by, 'agent', ''),
            'counts', COALESCE(sl.counts, '{}'::jsonb),
            'parentDocId', ss.parent_doc_id
          )
        ) as history
      FROM avocado.data_shipmentsnapshot ss
      LEFT JOIN snapshot_lines sl ON sl.shipment_snapshot_id = ss.id
      WHERE ss.shipment_id IN (SELECT shipment_id FROM filtered_shipments)
      GROUP BY ss.shipment_id
    ),
    -- Latest snapshot per shipment provides the GPS verification state used
    -- by the client-side "unverified" filter on LMD downloads (issue 24913).
    -- Tie-break with id DESC so the pick is deterministic when two snapshots
    -- share a created_at timestamp.
    gps_lookup AS (
      SELECT DISTINCT ON (shipment_id)
        shipment_id,
        is_gps_authenticated,
        gps_validation_fail_comment
      FROM avocado.data_shipmentsnapshot
      WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
      ORDER BY shipment_id, created_at DESC, id DESC
    ),
    -- hasKeyAndPassword is derived inside this CTE so the plaintext password
    -- and encryption key stay in Postgres. Downstream code only needs the
    -- boolean and the user fail reason to run the "unverified" filter.
    otp_lookup AS (
      SELECT
        shipment_id,
        (otp_key IS NOT NULL AND NULLIF(password, '') IS NOT NULL) AS has_key_and_password,
        user_fail_reason
      FROM avocado.shipment_otp
      WHERE shipment_id IN (SELECT shipment_id FROM filtered_shipments)
    )
    SELECT
      s.id,
      s.shipment_id,
      s.status,
      s.origin_id,
      s.destination_id,
      s.planning_type,
      s.route_id,
      CASE
        WHEN ol.group_id IS NOT NULL AND s.suborder_id IS NOT NULL
          THEN CONCAT('type:orderSnapshot:groupId:', ol.group_id, ':orderId:', s.order_id, ':suborderId:', s.suborder_id)
        ELSE s.order_id
      END as order_id,
      s.created_at,
      s.created_by,
      s.updated_at,
      s.updated_by,
      s.notes,
      s.funder_id,
      s.program_id,
      sda.snapshot_dates,
      dl.driver_name,
      ha.history,
      r.name as route_name,
      org.name AS carrier_name,
      gl.is_gps_authenticated,
      gl.gps_validation_fail_comment,
      -- shipment_otp.shipment_id is UNIQUE so otp_lookup is one-to-one.
      -- otp_shipment_id doubles as an existence marker: NULL means there is
      -- no OTP row and the caller must NOT synthesise an otp object (issue 24913).
      ol_otp.shipment_id AS otp_shipment_id,
      ol_otp.has_key_and_password AS otp_has_key_and_password,
      ol_otp.user_fail_reason AS otp_user_fail_reason,
     COALESCE(
      json_agg(
        jsonb_build_object(
          'id', sl.id,
          'created_at', sl.created_at,
          'updated_at', sl.updated_at,
          'sku', sl.sku,
          'amount', sl.amount,
          'shipment_id', sl.shipment_id,
          'order_invoicing_type', sl.order_invoicing_type,
          'origin_sku', sl.origin_sku,
          'destination_sku', sl.destination_sku
        )
        -- FILTER ensures that if the LEFT JOIN returns NULL (when shipment has no lines),
        -- we don't aggregate a NULL object into the array (avoids creating '[null]')
      ) FILTER (WHERE sl.id IS NOT NULL), '[]'
    ) AS lines
    FROM avocado.data_shipment s
    LEFT JOIN avocado.data_route r ON s.route_id = r.couchdb_id
    LEFT JOIN avocado.data_organisation org ON r.carrier_id = org.id
    LEFT JOIN avocado.data_shipmentline sl ON s.id = sl.shipment_id
    LEFT JOIN order_lookup ol ON ol.order_id = s.order_id
    LEFT JOIN snapshot_dates_agg sda ON sda.shipment_id = s.shipment_id
    LEFT JOIN driver_lookup dl ON dl.shipment_id = s.shipment_id
    LEFT JOIN history_agg ha ON ha.shipment_id = s.shipment_id
    LEFT JOIN gps_lookup gl ON gl.shipment_id = s.shipment_id
    LEFT JOIN otp_lookup ol_otp ON ol_otp.shipment_id = s.shipment_id
    WHERE ${whereClause}
    GROUP BY
    s.id, s.shipment_id, s.status, s.origin_id, s.destination_id,
    s.planning_type, s.route_id, s.order_id, s.created_at, s.created_by,
    s.updated_at, s.updated_by, s.notes, s.funder_id, s.program_id,
    r.name, org.name, ol.group_id, sda.snapshot_dates, dl.driver_name,
    ha.history, gl.is_gps_authenticated, gl.gps_validation_fail_comment,
    ol_otp.shipment_id, ol_otp.has_key_and_password, ol_otp.user_fail_reason
    ORDER BY s.created_at DESC, s.shipment_id ASC
  `
    try {
      const { rows } = await this.pgConnection.query(query, params)
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }

  /**
   * Gets shipment details for a given shipment ID. Including sku and price details
   * @param {string} shipmentId - Couch shipmentId minus status. Required.
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of shipment details.
   */
  async getShipmentWithDetails (shipmentId) {
    // Includes per-line batch details so downstream integrations (e.g. Shipsy returns) can send batch-level quantities.
    const query = `
        select
          ds.shipment_number as "shipmentNumber",
          ds.origin_id as "originId",
          ds.destination_id as "destinationId",
          ds.created_at as "createdAt",
          ds.status,
          ds.planning_type as "planningType",
          COALESCE(dss2.original_parent_doc_id, dss.parent_doc_id) as "parentDocId",
          ds.is_automatic_return as "isAutomaticReturn",
          dsl.id as "shipmentLineId",
          dsl.origin_sku as "originSku",
          dsl.destination_sku as "destinationSku",
          dsl.amount,
          (
            select sellprice
            from real.sellprice_daily
            where sku = dsl.sku and date = ds.created_at::date
            order by created_at desc nulls last
            limit 1
          ) as sellprice,
          (
            select jsonb_agg(jsonb_build_object(
              'batchId', dsl_detail.batch,
              'quantity', dsl_detail.amount
            ) order by dsl_detail.batch)
            from avocado.data_shipmentline_details dsl_detail
            where dsl_detail.shipment_line = dsl.id
          ) as "lineDetails",
          jsonb_build_object(
            'fsid', dlo.fsid,
            'code', dlo.code,
            'uuid', dlo.uuid,
            'classification', dlo.classification,
            'city', dlo.city,
            'name', dlo.name_of_pharmacy,
            'address', dlo.address,
            'state', dlo.state,
            'country', dlo.country,
            'status', dlo.active,
            'email', dlo.email,
            'ownerName', dlo.owners_name,
            'phoneNumber', dlo.owners_phone_number,
            'isCentral', dlo.is_central
          ) as origin,
          jsonb_build_object(
            'fsid', dld.fsid,
            'code', dld.code,
            'uuid', dld.uuid,
            'classification', dld.classification,
            'city', dld.city,
            'name', dld.name_of_pharmacy,
            'address', dld.address,
            'state', dld.state,
            'country', dld.country,
            'status', dld.active,
            'email', dld.email,
            'ownerName', dld.owners_name,
            'phoneNumber', dld.owners_phone_number,
            'isCentral', dld.is_central
          ) as destination
        from avocado.data_shipment ds
        left join avocado.data_shipmentline dsl
          on ds.id = dsl.shipment_id
        left join lateral (
          select parent_doc_id
          from avocado.data_shipmentsnapshot ss
          where ss.shipment_id = ds.shipment_id
            and ss.status = 'new'
          order by ss.created_at desc
          limit 1
        ) dss on true
        -- For returns of invoice-callback follow-up shipments, the follow-up's new snapshot
        -- carries a parent_doc_id pointing to the original delivery's shipment ID. COALESCE
        -- resolves one level up the chain so the payload always references the shipment
        -- Shipsy originally knows about, not an internally-created follow-up.
        -- split_part normalises both full snapshot IDs (containing ':status:') and bare
        -- shipment IDs to the same shipment-id form before the lookup.
        left join lateral (
          select parent_doc_id as original_parent_doc_id
          from avocado.data_shipmentsnapshot ss2
          where ss2.shipment_id = split_part(dss.parent_doc_id, ':status:', 1)
            and ss2.status = 'new'
          order by ss2.created_at desc
          limit 1
        ) dss2 on true
        left join avocado.data_location dlo
          on ds.origin_id = dlo.fsid
        left join avocado.data_location dld
          on ds.destination_id = dld.fsid
        where ds.shipment_id = $1
      `
    try {
      const { rows } = await this.pgConnection.query(query, [shipmentId])
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }

  /**
   * Fetches unitVolume values for a list of product IDs from the native product tables.
   * Used as a building block for volume calculation when postgres line tables are empty.
   * @param {string[]} productIds - e.g. ['product:ghsc-aid0015', 'product:ghsc-aid0015h']
   * @returns {Promise<Object>} Map of { productId: unitVolume }
   */
  async getUnitVolumesForProductIds (productIds) {
    if (!productIds || productIds.length === 0) {
      return {}
    }
    const normalizedSkus = productIds.map(id => id.toLowerCase().replace(/^product:/, ''))
    const { rows } = await this.pgConnection.query(`
      SELECT DISTINCT ON (LOWER(ps.old_sku_id))
        'product:' || LOWER(ps.old_sku_id) AS product_id,
        pp.volume::DOUBLE PRECISION AS unit_volume
      FROM avocado.data_productsku ps
      JOIN avocado.data_productpresentation pp ON pp.id = ps.presentation_id
      WHERE LOWER(ps.old_sku_id) = ANY($1)
        AND ps._deleted IS NOT TRUE
        AND pp.volume IS NOT NULL
      ORDER BY LOWER(ps.old_sku_id), ps.id ASC
    `, [normalizedSkus])
    return rows.reduce((acc, row) => {
      acc[row.product_id] = row.unit_volume
      return acc
    }, {})
  }

  /**
   * Gets a shipment row by shipment_number
   * This is a new function that gets shipments by their shipment numbers.
   * For shipments with the 12 alphanumeric chars, shipment numbers are guaranteed to be unique.
   * @param {string} shipmentNumber
   * @returns {Promise<Array>}
   */
  async getShipmentByNumber (shipmentNumber) {
    const query = `
      select
        ds.*,
        dsl.sku,
        dsl.amount,
        dsl.origin_sku,
        dsl.destination_sku
      from ${DATA_SHIPMENT_TABLE_NAME} ds
      left join ${DATA_SHIPMENTLINE_TABLE_NAME} dsl
        on ds.id = dsl.shipment_id
      where ds.shipment_number = $1
    `
    const { rows } = await this.pgConnection.query(query, [shipmentNumber])
    return rows
  }

  /**
   * Gets all central warehouses
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of all central warehouses with their product market code.
   */
  async getCentralWarehouses () {
    const query = `
      select
        dl.uuid,
        dl.fsid,
        dl.code,
        dl.name_of_pharmacy,
        dl.level,
        dl.active,
        dl.market,
        dl.market_id,
        dl.orders_status,
        dl.classification,
        dm.product_market_code as "productMarketCode"
      from avocado.data_location dl
      left join avocado.data_market dm
        on dm.id = dl.market_id
      where is_central = true
    `
    try {
      const { rows } = await this.pgConnection.query(query)
      return rows
    } catch (error) {
      throw new Error(`Error executing query: ${error.message}`)
    }
  }
}

module.exports = DataShipmentAdapter
